import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { parseISO } from "date-fns";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GetRecordComposerDefinitionDetailDocument } from "~/common/gqls";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import {
  type StoredKeyRename,
  type StoredRetype,
  storedToForm,
  zRecordUpdateComposerDefinition,
} from "~/features/dataImportProjects/utils/recordUpdateOverride";
import { useTenant } from "~/features/tenant/context";
import { v2Route } from "~/v2/routes";
import { RecordUpdateOverrideEditForm } from "./RecordUpdateOverrideEditForm";

interface DirectiveScopeProps {
  categoryIds: string[] | undefined;
  composerIds: string[] | undefined;
}

function DirectiveScope({ categoryIds, composerIds }: DirectiveScopeProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 text-12-regular text-fg-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span>{t("feature.dataImportProjects.recordUpdateDirectives.categories")}</span>
        {categoryIds?.length ? (
          categoryIds.map((categoryId) => (
            <Link
              key={categoryId}
              to={v2Route.admin.categories.detail.$buildPath({ params: { id: categoryId } })}
              className="font-mono text-primary-700 hover:underline"
            >
              {categoryId}
            </Link>
          ))
        ) : (
          <span>{t("feature.dataImportProjects.recordUpdateDirectives.allCategories")}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span>{t("feature.dataImportProjects.recordUpdateDirectives.composers")}</span>
        {composerIds?.length ? (
          composerIds.map((composerId) => (
            <span key={composerId} className="font-mono text-fg-2">
              {composerId}
            </span>
          ))
        ) : (
          <span>{t("feature.dataImportProjects.recordUpdateDirectives.allComposers")}</span>
        )}
      </div>
    </div>
  );
}

interface KeyRenameDirectivesProps {
  directives: StoredKeyRename[];
}

function KeyRenameDirectives({ directives }: KeyRenameDirectivesProps) {
  const { t } = useTranslation();
  if (directives.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-14-bold text-fg-1">{t("feature.dataImportProjects.recordUpdateDirectives.keyRenames")}</h3>
      <ul className="space-y-2">
        {directives.map((directive, index) => (
          <li
            key={`${directive.from}-${directive.to}-${index}`}
            className="space-y-1 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-3"
          >
            <div className="flex items-center gap-2 font-mono text-14-regular text-fg-1">
              <span>{directive.from}</span>
              <span aria-hidden className="text-fg-3">
                →
              </span>
              <span>{directive.to}</span>
            </div>
            <DirectiveScope categoryIds={directive.categories} composerIds={directive.composers} />
          </li>
        ))}
      </ul>
    </section>
  );
}

interface RetypeDirectivesProps {
  directives: StoredRetype[];
}

function RetypeDirectives({ directives }: RetypeDirectivesProps) {
  const { t } = useTranslation();
  if (directives.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-14-bold text-fg-1">{t("feature.dataImportProjects.recordUpdateDirectives.retypes")}</h3>
      <ul className="space-y-2">
        {directives.map((directive, index) => (
          <li
            key={`${directive.key}-${directive.fromType}-${directive.toType}-${index}`}
            className="space-y-1 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-3"
          >
            <div className="font-mono text-14-regular text-fg-1">{directive.key}</div>
            <div className="flex items-center gap-2 text-12-regular text-fg-2">
              <span>{directive.fromType}</span>
              <span aria-hidden className="text-fg-3">
                →
              </span>
              <span>{directive.toType}</span>
            </div>
            {directive.fromAttributeType !== undefined || directive.toAttributeType !== undefined ? (
              <div className="flex items-center gap-2 text-12-regular text-fg-2">
                <span>{t("feature.dataImportProjects.recordUpdateDirectives.attributeType")}</span>
                <span>
                  {directive.fromAttributeType ?? t("feature.dataImportProjects.recordUpdateDirectives.none")}
                </span>
                <span aria-hidden className="text-fg-3">
                  →
                </span>
                <span>{directive.toAttributeType ?? t("feature.dataImportProjects.recordUpdateDirectives.none")}</span>
              </div>
            ) : null}
            <DirectiveScope categoryIds={directive.categories} composerIds={directive.composers} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function OverridePairsTable({ title, rows }: { title: string; rows: { key: string; template: string }[] }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-14-bold text-fg-1">{title}</h3>
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
        <table className="w-full min-w-[480px] border-collapse text-12-regular">
          <thead className="bg-bg-subtle text-fg-2">
            <tr>
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("feature.dataImportProjects.recordUpdateOverride.key")}
              </th>
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("feature.dataImportProjects.recordUpdateOverride.template")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-border-default first:border-t-0">
                <td className="px-3 py-2 align-top font-mono text-fg-1">{r.key}</td>
                <td className="px-3 py-2 align-top font-mono text-fg-1">{r.template}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function RecordUpdateComposerDetailPanel({ id }: { id: string }) {
  const { t } = useTranslation();
  const { isReadOnly, projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(`record-update-composer:${id}`);
  const { tenantId } = useTenant();
  const [openEditForm, setOpenEditForm] = useState(false);

  useEffect(() => {
    if (isReadOnly) setOpenEditForm(false);
  }, [isReadOnly]);

  const { data, dataState } = useSuspenseQuery(
    GetRecordComposerDefinitionDetailDocument,
    tenantId ? { variables: { tenantId, id } } : skipToken,
  );

  const composerDef = tenantId && dataState === "complete" ? data?.recordComposerDefinitions[0] : undefined;
  const dipId = composerDef?.dataImportProjectRecordComposerDefinition?.dataImportProjectId;
  const isAuthorized = dipId === projectId;
  setAuthorized(isAuthorized);

  if (!composerDef || !dipId || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  const parsed = zRecordUpdateComposerDefinition.safeParse(composerDef.definition);
  const form = storedToForm(parsed.success ? parsed.data.overrides : undefined);
  const keyRenames = parsed.success ? (parsed.data.keyRenames ?? []) : [];
  const retypes = parsed.success ? (parsed.data.retypes ?? []) : [];
  const isEmpty =
    form.entity.length === 0 &&
    form.relation.length === 0 &&
    !form.blockedReason &&
    keyRenames.length === 0 &&
    retypes.length === 0;

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[18px] font-semibold text-fg-1">{composerDef.name}</h2>
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-fg-3">
              <div className="flex gap-1.5">
                <dt>{t("model.common.id")}:</dt>
                <dd className="font-mono">{composerDef.id}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.updatedAt")}:</dt>
                <dd>{composerDef.updatedAt ? t("date.short", { date: parseISO(composerDef.updatedAt) }) : "-"}</dd>
              </div>
            </dl>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isReadOnly || !form.editable}
            onClick={() => {
              if (!canExecuteMutation()) return;
              setOpenEditForm(true);
            }}
          >
            <Pencil className="size-3.5" />
            {t("ui.edit")}
          </Button>
        </div>
      </section>

      <p className="rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-3 text-14-regular text-fg-2">
        {t("feature.dataImportProjects.recordUpdateOverride.explainer")}
      </p>

      {form.blockedReason ? (
        <div className="rounded-[var(--radius-md)] border border-score-b bg-score-b-bg p-3 text-14-regular text-[#B25612]">
          {t("feature.dataImportProjects.recordUpdateOverride.blocked")}
        </div>
      ) : null}

      <OverridePairsTable title={t("feature.dataImportProjects.recordUpdateOverride.entity")} rows={form.entity} />
      <OverridePairsTable title={t("feature.dataImportProjects.recordUpdateOverride.relation")} rows={form.relation} />
      <KeyRenameDirectives directives={keyRenames} />
      <RetypeDirectives directives={retypes} />
      {isEmpty ? (
        <p className="text-14-regular text-fg-3">{t("feature.dataImportProjects.recordUpdateOverride.none")}</p>
      ) : null}

      <Dialog
        open={openEditForm && !isReadOnly}
        onOpenChange={(open) => {
          if (!open || canExecuteMutation()) setOpenEditForm(open);
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("feature.dataImportProjects.recordUpdateOverride.editTitle")}</DialogTitle>
          </DialogHeader>
          {openEditForm && !isReadOnly ? (
            <RecordUpdateOverrideEditForm
              recordUpdateComposerDefinitionId={composerDef.id}
              initialForm={form}
              onSaved={() => setOpenEditForm(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
